import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetThemeDetail, getServerPageGetThemeSlugs } from 'api/graphql-helper';
import { PageInformationFieldsFragment, ThemeDetailFieldsFragment, ThemeFieldsFragment } from 'api/graphql-types';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import ExpandedHeader from 'modules/expanded-header/expanded-header';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IThemeDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	themeBySlug?: ThemeFieldsFragment;
	themeDetail?: ThemeDetailFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const ThemeDetailPage = (props: IThemeDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.themeBySlug || null,
		pageQuery: () => getServerPageGetThemeDetail({ variables: props.variables }, client)
	});

	const modules = page?.modules || [];
	const globalModules = props.themeDetail?.modules || [];
	const seoSingle = props?.pageInformation?.seo;
	const slug = page?.slug;

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={seoSingle} />
			{page && page.__typename === 'Theme' ? (
				<>
					<ExpandedHeader module={page.header} slug={slug || '/'} />
					<ModuleBuilder modules={[...modules, ...globalModules]} slug={slug || '/'} />
				</>
			) : (
				<PasswordLayout locale={props.variables?.locale} slug={props.variables?.slug}>
					<PasswordModule />
				</PasswordLayout>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetThemeSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.THEMES.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.THEMES.slug}/${ctx.params.slug}` : '/';
		const variables = {
			slug,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::theme.theme',
			contentTypeName: 'theme',
			fetchPage: () => getServerPageGetThemeDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.themeBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			},
			neutralStyle: false,
			isSubsite: false
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				themeBySlug: passwordResult.page,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in theme detail', error);

		return {
			notFound: true
		};
	}
};

export default ThemeDetailPage;
